import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CTA from '../components/CTA';
import OurCommitment from '../components/OurCommitment';
import { generateCTAMessage } from '../utils/ctaMessages.js';

const TestimonialsPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "Adam Stansell",
      position: "Founder & CEO",
      company: "Healthcare Startup",
      industry: "Healthcare",
      rating: 5,
      project: "AxleFlow - Intelligent Transport Dispatch System",
      testimonial: "We brought them in to automate our patient transport queries. One engineer worked side by side with our dispatcher and built an assistant that now handles about 80% of routine requests. The turnaround on complex cases is much cleaner now. Our average response time dropped by 60%. It feels like something built for our actual workflows, not just a demo.",
      avatar: "AS",
      verified: true,
      platform: "Direct Client"
    },
    {
      id: 2,
      name: "Tarek Kabrit",
      position: "Co-founder & CEO",
      company: "Transportation Company",
      industry: "Transportation",
      rating: 5,
      project: "SeezAuto - AI Sales Assistant for Car Leasing",
      testimonial: "We needed a simple way for our sales reps to get answers without digging through documentation. The AI copilot has been a quiet helper that reduced back and forth communication, provided faster quotes, and gave us a steady increase in conversions. The integration with our CRM was smooth and the team remained responsive after the project went live.",
      avatar: "TK",
      verified: true,
      platform: "Upwork"
    },
    {
      id: 3,
      name: "Mike Thompson",
      position: "IT Director",
      company: "Cybersecurity Firm",
      industry: "Cybersecurity",
      rating: 5,
      project: "SecureMail Pro - Advanced Email Security Suite",
      testimonial: "Our security operations center was overwhelmed with alerts. They built a classifier that reduced noise without missing real threats. We are now blocking more malicious emails and spending much less time dealing with false positives. This is one of those rare projects where both the dashboard and daily operations improved significantly.",
      avatar: "MT",
      verified: true,
      platform: "Upwork"
    },
    {
      id: 4,
      name: "Jennifer Park",
      position: "E‑commerce Manager",
      company: "Retail Platform",
      industry: "Retail",
      rating: 5,
      project: "ShopSmart AI - Personalized Shopping Engine",
      testimonial: "One engineer from their team delivered the first version in about 10 days, then we worked together weekly to improve it. The recommendations are sensible and not overwhelming. Our merchandising team can easily adjust the rules. It has made a real impact without becoming another system that requires constant maintenance.",
      avatar: "JP",
      verified: true,
      platform: "Direct Client"
    },
    {
      id: 5,
      name: "Will Yin",
      position: "Co-founder & CEO",
      company: "Financial Services",
      industry: "Finance",
      rating: 5,
      project: "MandolinKYC - Smart Document Verification System",
      testimonial: "We wanted to reduce repetitive work during customer onboarding. A single engineer worked with our operations lead and built OCR with validation systems. Reviews are now faster, exceptions are clearer, and audit requests are easier to handle. The boring work became manageable in a good way.",
      avatar: "WY",
      verified: true,
      platform: "Upwork"
    },
    {
      id: 6,
      name: "Lisa Wang",
      position: "Security Analyst",
      company: "Security Company",
      industry: "Cybersecurity",
      rating: 5,
      project: "SMSGuard - Mobile Security Protection Suite",
      testimonial: "They did not oversell their capabilities. We provided them with a messy dataset and they delivered a model we could trust along with a clean review process. Security incidents are down and our triage process is much calmer. This is exactly what we needed.",
      avatar: "LW",
      verified: true,
      platform: "Direct Client"
    },
    {
      id: 7,
      name: "Robert Martinez",
      position: "Career Coach",
      company: "Education Platform",
      industry: "Education",
      rating: 5,
      project: "InterviewPro - AI-Powered Career Coach",
      testimonial: "The team was excellent to work with. They truly understood what our clients needed and delivered a polished, user friendly application. The desktop interview assistant has been very successful. We have seen 70% higher engagement and the AI coaching is incredibly helpful. Our clients love it.",
      avatar: "RM",
      verified: true,
      platform: "Upwork"
    },
    {
      id: 8,
      name: "Amanda Foster",
      position: "Data Analyst",
      company: "Manufacturing Company",
      industry: "Manufacturing",
      rating: 5,
      project: "ERP Data Copilot",
      testimonial: "This team really delivered. Being able to ask questions about our ERP data in plain English has been revolutionary. We are getting insights 50% faster and our team can focus on analysis instead of writing SQL queries. The LangChain implementation is robust and reliable.",
      avatar: "AF",
      verified: true,
      platform: "Direct Client"
    },
    {
      id: 9,
      name: "Carlos Rodriguez",
      position: "Operations Director",
      company: "Financial Services",
      industry: "Finance",
      rating: 5,
      project: "DocuMind AI - Intelligent Document Assistant",
      testimonial: "The team was excellent to work with. The PDF chatbot has transformed how we search through our document library. The retrieval accuracy improved by 40% and having citations makes the answers trustworthy. The Milvus vector database integration works perfectly.",
      avatar: "CR",
      verified: true,
      platform: "Upwork"
    },
    {
      id: 10,
      name: "Emma Wilson",
      position: "Product Manager",
      company: "Entertainment Platform",
      industry: "Retail",
      rating: 5,
      project: "CineMatch AI - Smart Entertainment Discovery",
      testimonial: "Working with this team was smooth and professional. Our conversational movie discovery feature has been very successful. Users love being able to describe what they want to watch naturally. The 90% plus query understanding rate is impressive and our user satisfaction has increased significantly.",
      avatar: "EW",
      verified: true,
      platform: "Direct Client"
    }
  ];

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i 
        key={i} 
        className={`bi ${i < rating ? 'bi-star-fill' : 'bi-star'}`}
        style={{ color: i < rating ? '#fbbf24' : '#d1d5db' }}
      ></i>
    ));
  };

  const getIndustryColor = (industry) => {
    const colors = {
      'Transportation': 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
      'Education': 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      'Cybersecurity': 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
      'Retail': 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
      'Finance': 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      'Manufacturing': 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)'
    };
    return colors[industry] || 'linear-gradient(135deg, #6b7280 0%, #374151 100%)';
  };

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '3px',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        zIndex: 9999,
        transition: 'width 0.1s ease-out'
      }}></div>

      {/* Breadcrumb Navigation */}
      <div style={{
        position: 'fixed',
        top: '65px',
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(14, 165, 233, 0.1)',
        padding: '1rem 0',
        zIndex: 999,
        transition: 'all 0.3s ease'
      }}>
        <div className="container">
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Link to="/" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              <i className="bi bi-house"></i>
              <span>Home</span>
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <Link to="/insights" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              Insights
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <span style={{
              color: '#0ea5e9',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              Testimonials
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="testimonials-hero-section" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '130px'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>


        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">

              
              {/* Industry Icon */}
              <div className="industry-icon-large mb-4" style={{
                display: 'inline-block',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '2px solid rgba(14, 165, 233, 0.3)',
                borderRadius: '50%',
                padding: '2rem',
                backdropFilter: 'blur(15px)',
                boxShadow: '0 8px 25px rgba(14, 165, 233, 0.2)'
              }}>
                <span style={{ fontSize: '4rem' }}>💬</span>
              </div>

              {/* Main Title */}
              <h1 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '800',
                color: '#ffffff',
                marginBottom: '2rem',
                lineHeight: '1.2',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
              }}>
                Client Testimonials
              </h1>
              
              {/* Subtitle */}
              <h2 style={{
                fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: '400',
                lineHeight: '1.4',
                marginBottom: '1rem'
              }}>
                Real feedback from satisfied clients across industries
              </h2>

              {/* Description */}
              <p style={{
                fontSize: '1.1rem',
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '800px',
                margin: '0 auto 3rem',
                lineHeight: '1.6'
              }}>
                Discover how our AI solutions have delivered measurable results and transformed operations for businesses worldwide.
              </p>

              {/* CTA Buttons */}
              <div className="hero-cta" style={{
                display: 'flex',
                gap: '1.5rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '3rem'
              }}>
                <Link to="/contact" style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '1.4rem 3.5rem',
                  borderRadius: '60px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                  minWidth: '220px',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.5), 0 10px 30px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)';
                }}>
                  <i className="bi bi-rocket-takeoff"></i>
                  Start Your Project
                </Link>

                <Link to="/case-studies" style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '2px solid rgba(14, 165, 233, 0.3)',
                  color: '#ffffff',
                  padding: '1.4rem 3.5rem',
                  borderRadius: '60px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(20px)',
                  minWidth: '220px',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.borderColor = 'rgba(14, 165, 233, 0.6)';
                  e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.2), 0 10px 30px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.target.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                  e.target.style.boxShadow = 'none';
                }}>
                  <i className="bi bi-file-text"></i>
                  View Case Studies
                </Link>
              </div>
              
              {/* Stats Grid */}
              <div style={{ marginBottom: '3rem' }}>
                <div className="row g-4 justify-content-center">
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-people-fill"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>10+</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Happy Clients</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-star-fill"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>5.0</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Average Rating</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-check-circle-fill"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>100%</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Project Success</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-building"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>6</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Industries Served</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '2rem'
              }}>
                <a href="#testimonials-grid" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#1e293b',
                  padding: '1.2rem 3rem',
                  borderRadius: '50px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2), 0 4px 15px rgba(0, 0, 0, 0.1)',
                  minWidth: '220px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(255, 255, 255, 0.3), 0 8px 25px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.2), 0 4px 15px rgba(0, 0, 0, 0.1)';
                }}>
                  <i className="bi bi-chat-dots"></i>
                  Read Testimonials
                </a>
                
                <Link to="/contact" style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: '1.2rem 3rem',
                  borderRadius: '50px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(15px)',
                  minWidth: '220px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}>
                  <i className="bi bi-calendar3"></i>
                  Start Your Project
                </Link>
              </div>
              
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
        `}</style>
      </section>

      {/* Testimonials Grid */}
      <section id="testimonials-grid" className="testimonials-grid-section">
        <div className="container">
          {/* Section Header */}
          <div className="testimonials-header">
            <h2 className="testimonials-title">Real Feedback from Real Projects</h2>
            <p className="testimonials-subtitle">Discover how our AI solutions have transformed businesses across different industries and company sizes.</p>
          </div>
          
          <div className="row g-4">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="col-lg-6">
                <div className="testimonial-card-enterprise">
                  {/* Header */}
                  <div className="testimonial-header">
                    <div className="testimonial-avatar">
                      {testimonial.avatar}
                    </div>
                    <div className="testimonial-info">
                      <div className="testimonial-name">{testimonial.name}</div>
                      <div className="testimonial-position">{testimonial.position}</div>
                      <div className="testimonial-company">{testimonial.company}</div>
                      <div className="testimonial-rating">
                        {getRatingStars(testimonial.rating)}
                      </div>
                    </div>
                  </div>

                  {/* Project Badge */}
                  <div className="testimonial-project-badge" style={{
                    background: getIndustryColor(testimonial.industry)
                  }}>
                    {testimonial.project}
                  </div>

                  {/* Testimonial Content */}
                  <div className="testimonial-content">
                    <blockquote className="testimonial-quote">
                      {testimonial.testimonial}
                    </blockquote>
                  </div>

                  {/* Verification Badge */}
                  {testimonial.verified && (
                    <div className="testimonial-verification-badge">
                      <i className="bi bi-patch-check-fill"></i>
                      Verified Client
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Our Commitment Section */}
      <div id="commitment">
        <OurCommitment />
      </div>

      {/* CTA Section */}
      <div id="cta">
        <CTA {...generateCTAMessage('testimonials')} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </>
  );
};

export default TestimonialsPage; 